// Lightweight fetch wrapper. All calls go to `/api/v1/*` on the same origin
// so the Vite dev server proxies them to the local Worker (wrangler dev).
// In production, Pages serves the React shell and the Worker's route pattern
// (`practiceword.inkyflow.com/api/v1/*`) handles the same paths.

export interface ApiError extends Error {
  status: number;
  code?: number;
  payload?: unknown;
}

export interface ApiOptions extends Omit<RequestInit, "body" | "credentials"> {
  body?: unknown;
  /** When true, throw on non-2xx (default true). */
  throwOnError?: boolean;
}

function makeError(message: string, status: number, payload?: unknown): ApiError {
  const err = new Error(message) as ApiError;
  err.name = "ApiError";
  err.status = status;
  err.payload = payload;
  if (payload && typeof payload === "object" && "code" in payload) {
    err.code = (payload as { code: number }).code;
  }
  return err;
}

/** Perform a JSON request to `/api/v1/{path}`. */
export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, throwOnError = true, headers, ...rest } = options;

  const init: RequestInit = {
    ...rest,
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const url = path.startsWith("/") ? path : `/${path}`;
  const fullUrl = url.startsWith("/api/") ? url : `/api/v1${url}`;

  const res = await fetch(fullUrl, init);

  // No-content responses
  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await res.json().catch(() => undefined) : await res.text();

  if (!res.ok) {
    const message =
      isJson && payload && typeof payload === "object" && "message" in payload
        ? String((payload as { message?: unknown }).message)
        : `Request failed (${res.status})`;
    if (throwOnError) throw makeError(message, res.status, payload);
  } else if (isJson && payload && typeof payload === "object" && "code" in payload) {
    // Mirror Worker convention: { code, data, message? }
    const wrapped = payload as { code: number; data?: T; message?: string };
    if (wrapped.code !== 0 && wrapped.code !== 200) {
      throw makeError(wrapped.message ?? `Business code ${wrapped.code}`, res.status, payload);
    }
    return wrapped.data as T;
  }

  return payload as T;
}
